import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';

const PAYMENT_LINK = 'https://rzp.io/rzp/nqOkpRd';
const LOCAL_KEY = 'isSubscribed';
const EMAIL_MAP_KEY = 'subscribedEmails';
const FEATURE_KEY = 'unlockedFeatures';
const PENDING_FEATURE_KEY = 'pendingFeature';
const CREDIT_KEY = 'featureCredits';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubscribedGlobal, setIsSubscribedGlobal] = useState(false);
  const [subscribedEmails, setSubscribedEmails] = useState(() => {
    const saved = localStorage.getItem(EMAIL_MAP_KEY);
    if (!saved) return {};
    try { return JSON.parse(saved); } catch (e) { return {}; }
  });
  const [unlockedFeatures, setUnlockedFeatures] = useState(() => {
    const saved = sessionStorage.getItem(FEATURE_KEY);
    if (!saved) return {};
    try { return JSON.parse(saved); } catch (e) { return {}; }
  });
  const [featureCredits] = useState(() => {
    const saved = sessionStorage.getItem(CREDIT_KEY);
    if (!saved) return {};
    try { return JSON.parse(saved); } catch (e) { return {}; }
  });

  const subscribe = useCallback(() => {
    window.location.href = PAYMENT_LINK;
  }, []);

  const subscribeForFeature = useCallback((feature) => {
    if (feature) {
      sessionStorage.setItem(PENDING_FEATURE_KEY, feature);
    }
    window.location.href = PAYMENT_LINK;
  }, []);

  const markSubscribedForEmail = useCallback((email) => {
    if (!email) {
      setIsSubscribedGlobal(true);
      localStorage.setItem(LOCAL_KEY, 'true');
      return;
    }
    setSubscribedEmails(prev => {
      const next = { ...prev, [email]: true };
      localStorage.setItem(EMAIL_MAP_KEY, JSON.stringify(next));
      return next;
    });
    setIsSubscribedGlobal(true);
    localStorage.setItem(LOCAL_KEY, 'true');
  }, []);

  const unlockFeature = useCallback((feature) => {
    if (!feature) return;
    setUnlockedFeatures(prev => {
      const next = { ...prev, [feature]: true };
      sessionStorage.setItem(FEATURE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const isFeatureUnlocked = useCallback((feature) => {
    if (!feature) return isSubscribedGlobal;
    const hasCredits = (featureCredits[feature] || 0) > 0;
    return Boolean(unlockedFeatures[feature]) || isSubscribedGlobal || hasCredits;
  }, [featureCredits, isSubscribedGlobal, unlockedFeatures]);

  const isSubscribedForEmail = useCallback((email) => {
    if (email) return Boolean(subscribedEmails[email]);
    return isSubscribedGlobal;
  }, [isSubscribedGlobal, subscribedEmails]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Accept both paid=1 and pad=1 as successful payment flags from any landing path
    const paidFlag = params.get('paid') === '1' || params.get('pad') === '1' || params.get('subscribed') === '1';
    const paymentDetected = paidFlag;
    const paidEmail = params.get('email');
    const pendingFeature = sessionStorage.getItem(PENDING_FEATURE_KEY);
    if (paymentDetected && paidEmail) {
      markSubscribedForEmail(paidEmail);
    } else if (paymentDetected && pendingFeature) {
      unlockFeature(pendingFeature);
      sessionStorage.removeItem(PENDING_FEATURE_KEY);
    } else if (paymentDetected) {
      setIsSubscribedGlobal(true);
      localStorage.setItem(LOCAL_KEY, 'true');
    }

    if (paymentDetected || paidEmail) {
      // After successful payment, drop query params and send user back to dashboard
      const target = '/dashboard';
      if (location.pathname !== target || location.search) {
        navigate(target, { replace: true });
      }
    }
  }, [location.pathname, location.search, navigate, markSubscribedForEmail, unlockFeature]);

  const value = useMemo(() => ({
    isSubscribed: isSubscribedForEmail,
    isSubscribedForEmail,
    subscribe,
    subscribeForFeature,
    markSubscribed: () => {
      setIsSubscribedGlobal(true);
      localStorage.setItem(LOCAL_KEY, 'true');
    },
    markSubscribedForEmail,
    paymentLink: PAYMENT_LINK,
    unlockFeature,
    isFeatureUnlocked,
  }), [
    isSubscribedForEmail,
    subscribe,
    subscribeForFeature,
    markSubscribedForEmail,
    unlockFeature,
    isFeatureUnlocked,
  ]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

SubscriptionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};
